import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { getOrdersInRange, OrderResponse } from "../../../api/order.api";
import { buildCsv, downloadCsv } from "../../../utils/csv";

type Period = "today" | "yesterday" | "last7" | "last30" | "week" | "month" | "year" | "custom";
type Bucket = "hour" | "day" | "month";
type Channel = "cafe" | "dessert";

const PERIODS: { id: Period; label: string }[] = [
  { id: "today",     label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7",     label: "Last 7 days" },
  { id: "last30",    label: "Last 30 days" },
  { id: "week",      label: "This week" },
  { id: "month",     label: "This month" },
  { id: "year",      label: "This year" },
  { id: "custom",    label: "Custom" },
];

// ---------- palette (local; mirrors stats page for consistency) ----------
const C = {
  espresso: "#5B3A29",
  caramel:  "#D97706",
  emerald:  "#059669",
  rose:     "#E11D48",
  amber:    "#F59E0B",
  sky:      "#0EA5E9",
  violet:   "#7C3AED",
};
const HERO_GRAD = "linear-gradient(135deg,#FFFBEB 0%,#FEE2C7 45%,#FECACA 100%)";

function bucketForSpan(start: moment.Moment, end: moment.Moment): Bucket {
  const days = end.diff(start, "days");
  if (days <= 1)  return "hour";
  if (days <= 62) return "day";
  return "month";
}

function rangeFor(p: Period, from: string, to: string): { start: string; end: string; bucketBy: Bucket } {
  const now = moment();
  if (p === "today") return { start: now.clone().startOf("day").toISOString(), end: now.clone().endOf("day").toISOString(), bucketBy: "hour" };
  if (p === "yesterday") {
    const y = now.clone().subtract(1, "day");
    return { start: y.clone().startOf("day").toISOString(), end: y.clone().endOf("day").toISOString(), bucketBy: "hour" };
  }
  if (p === "last7")  return { start: now.clone().subtract(6,  "days").startOf("day").toISOString(), end: now.clone().endOf("day").toISOString(), bucketBy: "day" };
  if (p === "last30") return { start: now.clone().subtract(29, "days").startOf("day").toISOString(), end: now.clone().endOf("day").toISOString(), bucketBy: "day" };
  if (p === "week")   return { start: now.clone().startOf("week").toISOString(),  end: now.clone().endOf("week").toISOString(),  bucketBy: "day"   };
  if (p === "month")  return { start: now.clone().startOf("month").toISOString(), end: now.clone().endOf("month").toISOString(), bucketBy: "day"   };
  if (p === "year")   return { start: now.clone().startOf("year").toISOString(),  end: now.clone().endOf("year").toISOString(),  bucketBy: "month" };
  const s = moment(from).startOf("day");
  const e = moment(to).endOf("day");
  return { start: s.toISOString(), end: e.toISOString(), bucketBy: bucketForSpan(s, e) };
}

function exportOrdersToCsv(orders: OrderResponse[], filename: string): void {
  const headers = [
    "Order ID", "Date", "Time", "Status", "Menu", "Category", "Channel",
    "Type", "Sweetness (%)", "Customer", "Cancelled by",
    "Brewed date", "Brewed time", "Ready date", "Ready time",
    "Picked date", "Picked time", "Remark",
  ];
  const rows = orders.map((o) => {
    const d = moment(o.orderDate);
    // @ts-ignore
    const brewedAt = o.brewedAt ? moment(o.brewedAt) : null;
    // @ts-ignore
    const readyAt = o.readyAt ? moment(o.readyAt) : null;
    // @ts-ignore
    const pickedAt = o.pickedAt ? moment(o.pickedAt) : null;
    // @ts-ignore
    const cancelledByName = o.cancelledBy?.name ?? "";
    return [
      o.id,
      d.format("YYYY-MM-DD"),
      d.format("HH:mm:ss"),
      o.status,
      o.menu?.name ?? "",
      o.menu?.category?.name ?? "",
      o.menu?.category?.categoryFor ?? "",
      o.type ?? "",
      o.sweetness ?? "",
      o.user?.name ?? "",
      cancelledByName,
      brewedAt ? brewedAt.format("YYYY-MM-DD") : "",
      brewedAt ? brewedAt.format("HH:mm:ss") : "",
      readyAt  ? readyAt.format("YYYY-MM-DD") : "",
      readyAt  ? readyAt.format("HH:mm:ss") : "",
      pickedAt ? pickedAt.format("YYYY-MM-DD") : "",
      pickedAt ? pickedAt.format("HH:mm:ss") : "",
      o.remark && o.remark !== "-" ? o.remark : "",
    ];
  });
  downloadCsv(filename, buildCsv(headers, rows));
}

function fmtDuration(ms: number): string {
  if (!isFinite(ms) || ms <= 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

type DashboardProps = {
  /** When true, also renders a "Top customers" section (admin view). */
  showCustomers?: boolean;
};

function DashBoard({ showCustomers = false }: DashboardProps) {
  const [period, setPeriod] = useState<Period>("today");
  const [channel, setChannel] = useState<Channel>("cafe");
  const [from, setFrom] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
  const [to, setTo]     = useState(moment().format("YYYY-MM-DD"));
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const { start, end, bucketBy } = useMemo(() => rangeFor(period, from, to), [period, from, to]);

  // Sync date inputs with active preset.
  useEffect(() => {
    if (period === "custom") return;
    const r = rangeFor(period, from, to);
    const f = moment(r.start).format("YYYY-MM-DD");
    const t = moment(r.end).format("YYYY-MM-DD");
    if (f !== from) setFrom(f);
    if (t !== to) setTo(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    setLoading(true);
    getOrdersInRange(start, end, 5000)
      .then((data) => setOrders(data ?? []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [start, end]);

  // Channel counts for the toggle badges (independent of channel filter).
  const channelCounts = useMemo(() => ({
    cafe:    orders.filter((o) => o.menu?.category?.categoryFor === "cafe").length,
    dessert: orders.filter((o) => o.menu?.category?.categoryFor === "dessert").length,
  }), [orders]);

  const channelOrders = useMemo(
    () => orders.filter((o) => o.menu?.category?.categoryFor === channel),
    [orders, channel],
  );

  const isDessert = channel === "dessert";

  const stats = useMemo(() => {
    const total = channelOrders.length;
    const counts = { ordered: 0, inProgress: 0, done: 0, picked: 0, cancelled: 0, rejected: 0 };
    let brewMs = 0, brewN = 0;
    let waitMs = 0, waitN = 0;
    for (const o of channelOrders) {
      switch (o.status) {
        case "ordered":     counts.ordered++; break;
        case "in-progress": counts.inProgress++; break;
        case "done":        counts.done++; break;
        case "picked":      counts.picked++; break;
        case "cancel":      counts.cancelled++; break;
        case "rejected":    counts.rejected++; break;
      }
      // @ts-ignore
      const b = o.brewedAt; // @ts-ignore
      const r = o.readyAt;  // @ts-ignore
      const p = o.pickedAt;
      if (b && r) { brewMs += new Date(r).getTime() - new Date(b).getTime(); brewN++; }
      if (r && p) { waitMs += new Date(p).getTime() - new Date(r).getTime(); waitN++; }
    }
    const completed = counts.done + counts.picked;
    return {
      total,
      counts,
      completed,
      cancelled: counts.cancelled + counts.rejected,
      completionRate:   total > 0 ? Math.round((completed / total) * 100) : 0,
      cancellationRate: total > 0 ? Math.round(((counts.cancelled + counts.rejected) / total) * 100) : 0,
      avgBrewMs: brewN > 0 ? brewMs / brewN : 0,
      avgWaitMs: waitN > 0 ? waitMs / waitN : 0,
    };
  }, [channelOrders]);

  // Trend buckets
  const buckets = useMemo(() => {
    const map = new Map<string, number>();
    if (bucketBy === "hour") {
      for (let h = 7; h <= 18; h++) map.set(String(h), 0);
    } else if (bucketBy === "day") {
      const cur = moment(start);
      const stop = moment(end);
      while (cur.isSameOrBefore(stop, "day")) {
        map.set(cur.format("YYYY-MM-DD"), 0);
        cur.add(1, "day");
      }
    } else {
      const cur = moment(start).startOf("month");
      const stop = moment(end).endOf("month");
      while (cur.isSameOrBefore(stop, "month")) {
        map.set(cur.format("YYYY-MM"), 0);
        cur.add(1, "month");
      }
    }
    for (const o of channelOrders) {
      const m = moment(o.orderDate);
      const k =
        bucketBy === "hour" ? String(m.hour()) :
        bucketBy === "day"  ? m.format("YYYY-MM-DD") :
        m.format("YYYY-MM");
      if (map.has(k)) map.set(k, (map.get(k) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([k, v]) => ({ key: k, count: v }));
  }, [channelOrders, bucketBy, start, end]);

  const maxBucket = Math.max(1, ...buckets.map((b) => b.count));
  const peakBucket = useMemo(
    () => buckets.reduce<{ key: string; count: number } | null>(
      (best, cur) => (!best || cur.count > best.count ? cur : best), null,
    ),
    [buckets],
  );

  const topMenus = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    for (const o of channelOrders) {
      const id = String(o.menu.id);
      if (!map.has(id)) map.set(id, { name: o.menu.name, count: 0 });
      map.get(id)!.count++;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [channelOrders]);
  const maxMenu = topMenus[0]?.count ?? 1;

  const topCustomers = useMemo(() => {
    const map = new Map<number, { id: number; name: string; count: number }>();
    for (const o of channelOrders) {
      const u = o.user as { id?: number; name?: string } | undefined;
      if (!u?.id) continue;
      if (!map.has(u.id)) map.set(u.id, { id: u.id, name: u.name ?? "Unknown", count: 0 });
      map.get(u.id)!.count++;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [channelOrders]);
  const maxCustomer = topCustomers[0]?.count ?? 1;

  const periodLabel = period === "custom"
    ? `${moment(start).format("D MMM YYYY")} → ${moment(end).format("D MMM YYYY")}`
    : PERIODS.find((p) => p.id === period)?.label ?? "";

  const liveTotal = stats.counts.ordered + stats.counts.inProgress + stats.counts.done;

  return (
    <div className="min-h-screen font-sans pb-[40px]" style={{ background: "#FAFAF7" }}>

      {/* ---------- Hero ---------- */}
      <header className="px-5 pt-[24px] pb-[16px]">
        <div
          className="rounded-3xl p-5 md:p-6 relative overflow-hidden"
          style={{ background: HERO_GRAD, boxShadow: "0 12px 36px -16px rgba(217,119,6,0.25)" }}
        >
          <div
            aria-hidden
            className="absolute -right-12 -top-12 w-[180px] h-[180px] rounded-full opacity-25"
            style={{ background: "radial-gradient(circle,#92400E 0%,transparent 70%)" }}
          />
          <div className="relative flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold tracking-[0.12em] uppercase" style={{ color: C.espresso }}>
                {isDessert ? "🍰 Dessert" : "☕ Cafe"} dashboard · {periodLabel}
              </p>
              <h1
                className="font-display font-black mt-2 leading-[0.95] tracking-tightest"
                style={{ color: "#1B0F08", fontSize: "clamp(36px,8vw,56px)" }}
              >
                {loading ? "…" : stats.total.toLocaleString()}
              </h1>
              <p className="font-semibold mt-1" style={{ color: C.espresso, fontSize: "14px" }}>
                {isDessert ? "desserts served" : "cups served"}
                {peakBucket && peakBucket.count > 0 && bucketBy === "hour" && (
                  <> · peak <span className="font-black">{peakBucket.key}:00</span> ({peakBucket.count})</>
                )}
              </p>
            </div>

            <button
              type="button"
              disabled={loading || channelOrders.length === 0}
              onClick={() => exportOrdersToCsv(
                channelOrders,
                period === "custom"
                  ? `woxa-cafe_${channel}_${moment(start).format("YYYYMMDD")}-${moment(end).format("YYYYMMDD")}.csv`
                  : `woxa-cafe_${channel}_${period}_${moment().format("YYYY-MM-DD-HHmm")}.csv`,
              )}
              className="inline-flex items-center gap-2 h-[40px] px-5 rounded-full text-sm font-bold text-white whitespace-nowrap shadow-sm disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97] transition-all"
              style={{ background: C.espresso }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>
      </header>

      {/* ---------- Channel toggle ---------- */}
      <div className="px-5 pb-3">
        <div className="flex gap-2 p-1 rounded-2xl bg-white" style={{ boxShadow: "0 0 0 1px #E7DFD2" }}>
          {([
            { id: "cafe",    label: "Drinks",   emoji: "☕", count: channelCounts.cafe },
            { id: "dessert", label: "Desserts", emoji: "🍰", count: channelCounts.dessert },
          ] as const).map((c) => {
            const active = channel === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setChannel(c.id)}
                className="flex-1 inline-flex items-center justify-center gap-2 h-[40px] rounded-xl text-sm font-bold transition-all"
                style={
                  active
                    ? { background: C.espresso, color: "#FFFBEB" }
                    : { background: "transparent", color: C.espresso }
                }
              >
                <span aria-hidden>{c.emoji}</span>
                <span>{c.label}</span>
                <span
                  className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[10px] font-black tabular-nums"
                  style={
                    active
                      ? { background: "rgba(255,251,235,0.25)", color: "#FFFBEB" }
                      : { background: "#F3EBDA", color: C.espresso }
                  }
                >
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------- Period chips + date inputs ---------- */}
      <div className="px-5 pb-4">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {PERIODS.map((p) => {
            const active = p.id === period;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className="inline-flex items-center h-[36px] px-4 rounded-full text-sm font-bold whitespace-nowrap transition-all"
                style={
                  active
                    ? { background: C.espresso, color: "#FFFBEB" }
                    : { background: "white", color: C.espresso, boxShadow: "0 0 0 1px #E7DFD2" }
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <label className="flex items-center gap-2 text-xs font-bold" style={{ color: C.espresso }}>
            <span className="opacity-70">From</span>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => { setFrom(e.target.value); setPeriod("custom"); }}
              className="h-9 px-3 rounded-xl bg-white border-2 text-sm focus:outline-none"
              style={{ borderColor: "#E7DFD2", color: "#1B0F08" }}
            />
          </label>
          <label className="flex items-center gap-2 text-xs font-bold" style={{ color: C.espresso }}>
            <span className="opacity-70">To</span>
            <input
              type="date"
              value={to}
              min={from}
              max={moment().format("YYYY-MM-DD")}
              onChange={(e) => { setTo(e.target.value); setPeriod("custom"); }}
              className="h-9 px-3 rounded-xl bg-white border-2 text-sm focus:outline-none"
              style={{ borderColor: "#E7DFD2", color: "#1B0F08" }}
            />
          </label>
        </div>
      </div>

      <main className="px-5 space-y-5">
        {/* ---------- Live queue — today only ---------- */}
        {period === "today" && (
          <section>
            <Eyebrow icon="⚡" color={C.amber}>
              Live queue · {liveTotal} active
            </Eyebrow>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <LiveTile
                label="Waiting"
                value={stats.counts.ordered}
                grad="linear-gradient(135deg,#60A5FA 0%,#2563EB 100%)"
                icon="⏳"
              />
              <LiveTile
                label="Brewing"
                value={stats.counts.inProgress}
                grad="linear-gradient(135deg,#FB923C 0%,#EA580C 100%)"
                icon="🔥"
                pulse
              />
              <LiveTile
                label="Ready"
                value={stats.counts.done}
                grad="linear-gradient(135deg,#34D399 0%,#059669 100%)"
                icon="✓"
              />
            </div>
          </section>
        )}

        {/* ---------- Summary KPIs ---------- */}
        <section>
          <Eyebrow icon="📊" color={C.caramel}>Summary</Eyebrow>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
            <StatCard tint={C.caramel} icon="📦" label="Total orders" value={stats.total} />
            <StatCard tint={C.emerald} icon="✓"  label="Completed"    value={stats.completed} sub={`${stats.completionRate}%`} />
            <StatCard tint={C.rose}    icon="✕"  label="Cancelled"    value={stats.cancelled} sub={`${stats.cancellationRate}%`} />
            <StatCard tint={C.amber}   icon="⏱"  label="Avg brew"     value={fmtDuration(stats.avgBrewMs)} mono sub="brewing → ready" />
          </div>
        </section>

        {/* ---------- Trend ---------- */}
        <section>
          <Eyebrow icon="📈" color={C.caramel}>
            {bucketBy === "hour" ? "Hour by hour" : bucketBy === "day" ? "Day by day" : "Month by month"}
          </Eyebrow>
          <div className="bg-white rounded-2xl p-4 mt-2" style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)" }}>
            {loading ? (
              <Spinner />
            ) : (
              <div className="flex items-end gap-1.5">
                {buckets.map((b) => {
                  const isPeak = b.key === peakBucket?.key && (peakBucket?.count ?? 0) > 0;
                  return (
                    <div key={b.key} className="flex-1 min-w-0 flex flex-col items-center">
                      <div
                        className="text-[10px] font-extrabold tabular-nums h-[14px] leading-[14px]"
                        style={{ color: isPeak ? C.caramel : "#A38B6A" }}
                      >
                        {b.count || ""}
                      </div>
                      <div className="w-full h-[150px] flex items-end">
                        <div
                          className="w-full rounded-t-lg transition-all duration-500"
                          style={{
                            height: `${(b.count / maxBucket) * 100}%`,
                            minHeight: b.count ? "6px" : "0",
                            background: isPeak
                              ? "linear-gradient(180deg,#F59E0B 0%,#D97706 100%)"
                              : "linear-gradient(180deg,#FDE68A 0%,#FBBF24 100%)",
                          }}
                        />
                      </div>
                      <div className="text-[10px] font-semibold mt-1" style={{ color: "#A38B6A" }}>
                        {bucketBy === "hour" ? b.key
                          : bucketBy === "day" ? moment(b.key).format("dd")[0]
                          : moment(b.key, "YYYY-MM").format("MMM")}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ---------- Top menus ---------- */}
        <section>
          <Eyebrow icon="🏆" color={C.caramel}>Top menus</Eyebrow>
          <div className="bg-white rounded-2xl mt-2 overflow-hidden" style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)" }}>
            {topMenus.length === 0 ? (
              <p className="px-4 py-5 text-sm text-center" style={{ color: "#A38B6A" }}>No data</p>
            ) : (
              topMenus.map((m, i) => (
                <div key={m.name} className="px-4 py-3 flex items-center gap-3" style={{ borderTop: i === 0 ? "0" : "1px solid #F3EBDA" }}>
                  <RankBadge rank={i + 1} />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-black text-[15px] truncate" style={{ color: "#1B0F08" }}>{m.name}</p>
                    <div className="mt-1.5 h-[8px] rounded-full overflow-hidden" style={{ background: "#F3EBDA" }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${(m.count / maxMenu) * 100}%`,
                          background:
                            i === 0 ? "linear-gradient(90deg,#F59E0B 0%,#D97706 100%)"
                          : i === 1 ? "linear-gradient(90deg,#A78BFA 0%,#7C3AED 100%)"
                          : i === 2 ? "linear-gradient(90deg,#FB923C 0%,#EA580C 100%)"
                          : `linear-gradient(90deg,${C.sky} 0%,${C.sky}CC 100%)`,
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-base font-black tabular-nums" style={{ color: C.espresso }}>{m.count}</span>
                </div>
              ))
            )}
          </div>
        </section>

        {/* ---------- Top customers (admin) ---------- */}
        {showCustomers && (
          <section>
            <Eyebrow icon="⭐" color={C.violet}>Top customers</Eyebrow>
            <div className="bg-white rounded-2xl mt-2 overflow-hidden" style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)" }}>
              {topCustomers.length === 0 ? (
                <p className="px-4 py-5 text-sm text-center" style={{ color: "#A38B6A" }}>No data</p>
              ) : (
                topCustomers.map((c, i) => {
                  const initial = c.name.trim().slice(0, 1).toUpperCase() || "?";
                  const bg = colorFromName(c.name);
                  return (
                    <div key={c.id} className="px-4 py-3 flex items-center gap-3" style={{ borderTop: i === 0 ? "0" : "1px solid #F3EBDA" }}>
                      <RankBadge rank={i + 1} />
                      <div
                        className="w-[40px] h-[40px] rounded-full text-white flex items-center justify-center font-display font-black flex-shrink-0 shadow-sm"
                        style={{ background: bg }}
                      >
                        {initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-black text-[15px] truncate" style={{ color: "#1B0F08" }}>{c.name}</p>
                        <div className="mt-1.5 h-[8px] rounded-full overflow-hidden" style={{ background: "#F3EBDA" }}>
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${(c.count / maxCustomer) * 100}%`, background: bg }}
                          />
                        </div>
                      </div>
                      <span className="text-base font-black tabular-nums" style={{ color: C.espresso }}>{c.count}</span>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

// ----------------------------- subcomponents -----------------------------

function colorFromName(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 70% 55%)`;
}

const Spinner: React.FC = () => (
  <div className="h-[160px] flex items-center justify-center">
    <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: "#D97706" }}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
      <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  </div>
);

const Eyebrow: React.FC<{ icon: string; color: string; children: React.ReactNode }> = ({ icon, color, children }) => (
  <p className="text-[11px] font-extrabold tracking-[0.10em] uppercase flex items-center gap-1.5" style={{ color }}>
    <span className="text-[14px] leading-none">{icon}</span>
    {children}
  </p>
);

const LiveTile: React.FC<{
  label: string; value: number; grad: string; icon: string; pulse?: boolean;
}> = ({ label, value, grad, icon, pulse }) => (
  <div
    className={`rounded-2xl p-4 text-white relative overflow-hidden ${pulse && value > 0 ? "animate-pulse-ring" : ""}`}
    style={{ background: grad, boxShadow: "0 8px 24px -12px rgba(0,0,0,0.3)" }}
  >
    <div className="flex items-start justify-between">
      <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] opacity-90">{label}</span>
      <span className="text-lg leading-none">{icon}</span>
    </div>
    <div className="font-display font-black tabular-nums leading-none mt-3" style={{ fontSize: "40px" }}>
      {value}
    </div>
  </div>
);

const StatCard: React.FC<{
  tint: string; icon: string; label: string; value: number | string; sub?: string; mono?: boolean;
}> = ({ tint, icon, label, value, sub, mono }) => (
  <div
    className="rounded-2xl bg-white p-4 relative overflow-hidden"
    style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 16px -12px rgba(91,58,41,0.18)" }}
  >
    <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl" style={{ background: tint }} />
    <div className="flex items-start justify-between">
      <p className="text-[10px] font-extrabold uppercase tracking-[0.10em]" style={{ color: tint }}>{label}</p>
      <span className="text-base leading-none">{icon}</span>
    </div>
    <p className={`font-display font-black mt-2 leading-[1] ${mono ? "tabular-nums" : ""}`} style={{ color: "#1B0F08", fontSize: "26px" }}>
      {value}
    </p>
    {sub && <p className="text-[11px] font-semibold mt-1" style={{ color: "#A38B6A" }}>{sub}</p>}
  </div>
);

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const palette =
    rank === 1 ? { bg: "linear-gradient(135deg,#FCD34D 0%,#D97706 100%)", color: "#1B0F08" }
    : rank === 2 ? { bg: "linear-gradient(135deg,#E5E7EB 0%,#9CA3AF 100%)", color: "#1B0F08" }
    : rank === 3 ? { bg: "linear-gradient(135deg,#FCA5A5 0%,#B45309 100%)", color: "#FFFFFF" }
    : { bg: "#F3EBDA", color: "#A38B6A" };
  return (
    <span
      className="w-[28px] h-[28px] rounded-full flex items-center justify-center text-xs font-black tabular-nums flex-shrink-0"
      style={{ background: palette.bg, color: palette.color }}
    >
      {rank}
    </span>
  );
};

export default DashBoard;
